var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { createClient, createConfig } from "@hey-api/client-fetch";
import { getGoogleReCaptchaToken } from "@jti/fe-clients";
import { DES_EVO_TENANT_TOKEN, DES_RECAPTCHA_TOKEN, DOB_INPUT_DATE, ISO_DATE_FORMAT, parseCustomDate, } from "@jti/fe-common";
import { sanitizeBaseUrl } from "../../../utils";
import { checkIfHasHiddenVerificationController, getAvailableVerificationMethodsController, initializeAsynchronousAgeVerificationController, performAgeVerificationController, } from "../../evo-client/__generated__";
import { getEvoVerificationStatus, getIsEvoVerificationPerformed } from "./utils";
export class AvpApiEvo {
    constructor(baseUrl, tenantToken, recaptchaId, isoCountryCode) {
        this.tenantToken = tenantToken;
        this.recaptchaId = recaptchaId;
        this.isoCountryCode = isoCountryCode;
        this.client = createClient(createConfig({ baseUrl: sanitizeBaseUrl(baseUrl), throwOnError: true }));
    }
    getAvailableMethods() {
        return __awaiter(this, void 0, void 0, function* () {
            const { data, response } = yield getAvailableVerificationMethodsController({
                client: this.client,
                headers: {
                    [DES_EVO_TENANT_TOKEN]: this.tenantToken,
                },
            });
            if (!(data === null || data === void 0 ? void 0 : data.data)) {
                throw new Error("EVO is missing the data field in the response related to the available methods");
            }
            return {
                data: data.data,
                statusCode: response.status,
            };
        });
    }
    isAgeVerificationHidden() {
        return __awaiter(this, void 0, void 0, function* () {
            var _a;
            const { data } = yield checkIfHasHiddenVerificationController({
                client: this.client,
                headers: {
                    [DES_EVO_TENANT_TOKEN]: this.tenantToken,
                },
            });
            if (!data) {
                throw new Error("EVO is missing the data field in the response related to the hidden verification");
            }
            // TODO: Return the correct type of checkIfHasHiddenVerificationController response
            return Boolean((_a = data.data) === null || _a === void 0 ? void 0 : _a.has_hidden_verification);
        });
    }
    verify(variables) {
        return __awaiter(this, void 0, void 0, function* () {
            var _a;
            const recaptchaToken = yield getGoogleReCaptchaToken(this.recaptchaId);
            const { user_email, fields } = variables, rest = __rest(variables, ["user_email", "fields"]);
            const requestBody = Object.assign(Object.assign(Object.assign({}, rest), { email: user_email }), (fields && {
                fields: Object.assign(Object.assign({}, fields), (fields.workflowName && { workflow_name: fields.workflowName })),
            }));
            const { data } = yield performAgeVerificationController({
                client: this.client,
                headers: {
                    [DES_EVO_TENANT_TOKEN]: this.tenantToken,
                    [DES_RECAPTCHA_TOKEN]: recaptchaToken,
                },
                body: requestBody,
            });
            const verificationData = data === null || data === void 0 ? void 0 : data.data;
            if (!verificationData) {
                throw new Error("EVO is missing the data field in the response related to the verification");
            }
            return {
                is_verified: Boolean(verificationData.is_verified),
                verification_performed: getIsEvoVerificationPerformed(verificationData.verification_status),
                uuid: (_a = verificationData.uuid) !== null && _a !== void 0 ? _a : "",
                status_from_provider: verificationData.verification_status,
                consumer_data: verificationData.consumer_data,
            };
        });
    }
    initAsyncVerification(_a) {
        return __awaiter(this, arguments, void 0, function* ({ methodCode, fields, userEmail, }) {
            var _b, _c;
            const recaptchaToken = yield getGoogleReCaptchaToken(this.recaptchaId);
            const requestBody = Object.assign({ email: userEmail, method_code: methodCode, country_code: this.isoCountryCode }, (fields && {
                fields: Object.assign(Object.assign(Object.assign({}, fields), (fields.dateOfBirth && {
                    dateOfBirth: parseCustomDate({
                        date: fields.dateOfBirth,
                        initialFormat: DOB_INPUT_DATE,
                        resultFormat: ISO_DATE_FORMAT,
                    }),
                })), (fields.workflowName && { workflow_name: fields.workflowName })),
            }));
            const { data } = yield initializeAsynchronousAgeVerificationController({
                client: this.client,
                headers: {
                    [DES_EVO_TENANT_TOKEN]: this.tenantToken,
                    [DES_RECAPTCHA_TOKEN]: recaptchaToken,
                },
                body: requestBody,
            });
            const evoData = data === null || data === void 0 ? void 0 : data.data;
            if (!evoData) {
                throw new Error("EVO is missing the data field in the response related to the async verification");
            }
            return {
                uuid: (_b = evoData === null || evoData === void 0 ? void 0 : evoData.uuid) !== null && _b !== void 0 ? _b : "",
                status: getEvoVerificationStatus(evoData === null || evoData === void 0 ? void 0 : evoData.verification_status),
                verification_performed: getIsEvoVerificationPerformed(evoData === null || evoData === void 0 ? void 0 : evoData.verification_status),
                data: (_c = evoData === null || evoData === void 0 ? void 0 : evoData.data) !== null && _c !== void 0 ? _c : {},
            };
        });
    }
}
//# sourceMappingURL=AvpApiEvo.js.map